import React from 'react';
import { useNavigate } from 'react-router';
import { formatBytes } from '@battleops/shared';
import { useServerFor, useServerNeeds } from '@battleops/client/src/data/useServer';
import Icon from './Icon';
import s from './ServerCard.module.css';

// The VPS's metrics (Lj, Oct 11: "the VPS project could have metrics for life — how much memory we're using, how much
// storage is left, uptime, etc."). Shared serverView over opsServer/now (the API writes it every 5 min from the host;
// owners only, so nobody else ever sees this card). Three pieces: the Server card on the VPS project, the one line on
// its pinned app card, and the Needs you rows on the All Home (disk < 10 % free, a container down, the backup > 8 d).

const TONE = { ok: 'var(--c-success)', warn: 'var(--c-gold)', bad: 'var(--c-danger, #FF7A70)' };
const fmtDate = (ms) => new Date(ms).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const fmtDay = (ms) => new Date(ms).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
const loadTxt = (v) => (v == null ? '—' : v.toFixed(2));

/** A 24 h line with gaps where a sample is missing; the last point dotted. */
function Spark({ points, color, max: fixedMax, label, format }) {
  const vals = points.map((p) => p.v).filter((v) => v != null);
  if (vals.length < 2) return <div className={s.sparkEmpty}>24 h history builds up</div>;
  const W = 240; const H = 40;
  const max = fixedMax || Math.max(1e-9, ...vals) * 1.15;
  const n = points.length;
  const x = (i) => (i * W) / Math.max(1, n - 1);
  const y = (v) => 3 + (H - 6) * (1 - Math.min(1, v / max));
  // one line + one filled area per run of samples (a missed slot is a gap, not a diagonal)
  const runs = []; let cur = null;
  points.forEach((p, i) => { if (p.v == null) { cur = null; return; } if (!cur) runs.push(cur = []); cur.push([x(i).toFixed(1), y(p.v).toFixed(1)]); });
  const d = runs.map((r) => r.map(([a, b], k) => `${k ? 'L' : 'M'}${a},${b}`).join('')).join(' ');
  const area = runs.map((r) => `M${r[0][0]},${H} ${r.map(([a, b]) => `L${a},${b}`).join('')} L${r[r.length - 1][0]},${H} Z`).join(' ');
  const lo = Math.min(...vals); const hi = Math.max(...vals);
  return (
    <svg className={s.spark} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={`${label}, last 24 hours: low ${format(lo)}, high ${format(hi)}, now ${format(vals[vals.length - 1])}`}>
      <path d={area} fill={color} fillOpacity=".12" />
      <path d={d} fill="none" stroke={color} strokeWidth="1.8" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
    </svg>
  );
}

function Bar({ pct, tone }) {
  return <div className={s.bar}><span style={{ width: `${Math.max(0, Math.min(100, pct || 0))}%`, background: tone }} /></div>;
}

function Check({ label, c }) {
  if (!c) return null;
  return (
    <div className={s.kv}><span>{label}</span><b style={{ color: c.ok ? TONE.ok : TONE.bad }}>{c.ok ? `healthy${c.ms != null ? ` · ${c.ms} ms` : ''}` : `down${c.code ? ` · HTTP ${c.code}` : ''}`}</b></div>
  );
}

function BackupRow({ label, b }) {
  if (!b) return <div className={s.kv}><span>{label}</span><b className={s.faint}>none found</b></div>;
  const old = b.ageDays != null && b.ageDays > 8;
  return (
    <div className={s.kv}>
      <span>{label}</span>
      <b style={{ color: b.ok ? undefined : old ? TONE.bad : TONE.warn }} title={[b.at ? fmtDate(b.at) : null, b.name, b.line].filter(Boolean).join('\n')}>
        {b.at ? fmtDay(b.at) : '—'}{b.bytes ? ` · ${formatBytes(b.bytes)}` : ''} · {b.ok ? 'ok' : old ? `${Math.floor(b.ageDays)} d old` : 'check'}
      </b>
    </div>
  );
}

/** The Server card on the VPS project page. Nothing when this isn't the server's project or the doc isn't here. */
export default function ServerCard({ project }) {
  const v = useServerFor(project);
  if (!v) return null;
  const state = v.red ? 'bad' : v.stale ? 'warn' : 'ok';
  const memTone = v.mem.pct >= 90 ? TONE.bad : v.mem.pct >= 75 ? TONE.warn : 'var(--pc, var(--c-accent))';
  return (
    <section className={s.card} data-state={state} aria-label="Server">
      <div className={s.head}>
        <span className={s.headIcon}><Icon name="monitor" size={17} /></span>
        <h2>Server</h2>
        {v.host ? <span className={s.host}>{v.host}</span> : null}
        <span className={s.pill} data-state={state}><span className={s.dot} />{v.red ? 'Needs attention' : v.stale ? `No reading for ${v.ageMin ?? '—'} min` : 'All good'}</span>
        <span className="spacer" />
        <span className={s.updated} title={v.at ? fmtDate(v.at) : ''}>updated {v.ago}</span>
      </div>

      {v.reasons.length ? (
        <div className={s.reasons}>
          {v.reasons.map((r, i) => <div key={i} className={s.reason}><Icon name="alert-triangle" size={14} /><b>{r.title}</b><span>{r.detail}</span></div>)}
        </div>
      ) : null}

      <div className={s.grid}>
        <div className={s.tile}>
          <div className={s.label}>Memory</div>
          <div className={s.big}>{v.mem.pct ?? '—'}<small>%</small></div>
          <div className={s.sub}>{formatBytes(v.mem.used)} of {formatBytes(v.mem.total)}{v.swap.total ? ` · swap ${v.swap.pct}%` : ''}</div>
          <Bar pct={v.mem.pct} tone={memTone} />
          <Spark points={v.history.mem} color={memTone} max={100} label="Memory" format={(x) => `${Math.round(x)}%`} />
        </div>

        <div className={s.tile}>
          <div className={s.label}>Load</div>
          <div className={s.big} style={v.load.hot ? { color: TONE.warn } : null}>{loadTxt(v.load.one)}</div>
          <div className={s.sub}>{v.load.cores ? `${v.load.cores} cores · ` : ''}5 min {loadTxt(v.load.five)} · 15 min {loadTxt(v.load.fifteen)}</div>
          <Spark points={v.history.load} color="var(--c-gold)" max={v.load.cores ? Math.max(v.load.cores, ...v.history.load.map((p) => p.v || 0)) : null} label="Load" format={loadTxt} />
        </div>

        <div className={s.tile}>
          <div className={s.label}>Storage</div>
          {v.disks.map((d) => (
            <div key={d.mount} className={s.disk}>
              <div className={s.diskHead}><b>{d.mount}</b><span style={d.red ? { color: TONE.bad } : d.warn ? { color: TONE.warn } : null}>{d.freePct}% free</span></div>
              <Bar pct={d.usedPct} tone={d.red ? TONE.bad : d.warn ? TONE.warn : 'var(--c-success)'} />
              <div className={s.sub}>{formatBytes(d.avail)} free of {formatBytes(d.size)}</div>
            </div>
          ))}
          {!v.disks.length ? <div className={s.sub}>No disks reported.</div> : null}
          <div className={s.kv} style={{ marginTop: 'auto' }}><span>Database</span><b>{formatBytes(v.db.bytes)}</b></div>
        </div>

        <div className={s.tile}>
          <div className={s.label}>Up</div>
          <div className={s.big}>{v.uptime}</div>
          <Check label="API" c={v.api} />
          <Check label="Caddy" c={v.caddy} />
          <BackupRow label="Offsite backup" b={v.backup.offsite} />
          <BackupRow label="Nightly dump" b={v.backup.nightly} />
        </div>
      </div>

      <div className={s.containers}>
        <div className={s.label}>Containers <span className={s.faint}>{v.up} up{v.down.length ? ` · ${v.down.length} down` : ''}</span></div>
        <div className={s.chips}>
          {v.containers.map((c) => (
            <span key={c.name} className={s.chip} data-state={c.down ? 'bad' : c.up ? 'ok' : 'off'} title={`${c.state}${c.health ? ` · ${c.health}` : ''} · restart ${c.restart}`}>
              <span className={s.dot} />{c.name}{c.down ? ` · ${c.health === 'unhealthy' ? 'unhealthy' : c.state}` : ''}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/** One line for the VPS's pinned app card: "RAM 61% · disk 72% free · up 12 d". */
export function ServerLine({ project }) {
  const v = useServerFor(project);
  if (!v || !v.oneLine) return null;
  return (
    <div className={s.line} data-state={v.red ? 'bad' : v.stale ? 'warn' : 'ok'} title={v.reasons.map((r) => r.title).join('\n') || `updated ${v.ago}`}>
      <Icon name="monitor" size={13} /><span>{v.oneLine}</span>{v.red && v.reasons.length ? <b className={s.lineCount}>{v.reasons.length} !</b> : null}
    </div>
  );
}

/** Needs you on the All Home: the server's alarms (none = nothing drawn). */
export function ServerNeeds() {
  const navigate = useNavigate();
  const rows = useServerNeeds();
  if (!rows.length) return null;
  return (
    <section className={s.needs} aria-label={`Needs you: ${rows.length}`}>
      <div className={s.needsHead}><span className={s.dot} /><h2>Needs you · Server</h2><span className={s.count}>{rows.length}</span></div>
      {rows.map((r, i) => (
        <button key={i} type="button" className={s.needRow} onClick={() => (r.projectId ? navigate(`/projects/${r.projectId}`) : null)}>
          <span className={s.needIcon}><Icon name={r.kind === 'server-disk' ? 'layers' : r.kind === 'server-backup' ? 'archive' : 'box'} size={16} /></span>
          <span className={s.needText}><b>{r.title}</b><span>{r.detail}</span></span>
          {r.projectId ? <Icon name="chevron-right" size={15} /> : null}
        </button>
      ))}
    </section>
  );
}
